import React, { useCallback, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Link,
  useLocation,
  useSearchParams,
  useParams,
  useNavigate,
} from "react-router-dom";
import { useClerk, useUser } from "@clerk/react";
import {
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  RocketLaunchIcon,
  UserGroupIcon,
  UsersIcon,
  FolderIcon,
  DocumentTextIcon,
  Cog6ToothIcon,
  ChevronRightIcon,
  ChevronUpDownIcon,
  ArrowRightStartOnRectangleIcon,
  PresentationChartBarIcon,
  BuildingOffice2Icon,
  SignalIcon,
  FlagIcon,
  ClipboardDocumentCheckIcon,
  MagnifyingGlassCircleIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from "@heroicons/react/24/outline";
import { AnimatedModal } from "../common/AnimatedModal";
import { cn } from "../../lib/utils";
import { friendlyCampaignName, generateCampaignKey } from "../../utils/helpers";
import { usePageAccess } from "../../contexts/CurrentUserContext";

// ---------------------------------------------------------------------------
// Type Definitions
// ---------------------------------------------------------------------------

interface SidebarUserInfo {
  name?: string;
  email?: string;
  initials?: string;
}

interface SidebarProps {
  showAdminSection?: boolean;
  showOverviewSection?: boolean;
  showManagementSection?: boolean;
  showCampaignSwitcher?: boolean;
  user?: SidebarUserInfo | null;
  onLogout?: (() => void) | null;
  className?: string;
  isMobileOpen?: boolean;
  onMobileToggle?: () => void;
  collapsed?: boolean;
  collapsible?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}

interface SidebarNavItemProps {
  to?: string;
  icon?: React.ReactNode;
  label: string;
  isActive?: boolean;
  onClick?: (() => void) | null;
  onMouseEnter?: () => void;
  external?: boolean;
  badge?: React.ReactNode;
  disabled?: boolean;
}

interface SidebarSectionProps {
  title?: string;
  children?: React.ReactNode;
  className?: string;
}

interface SidebarBrandProps {
  logo?: string;
  alt?: string;
  linkTo?: string;
  className?: string;
}

interface SidebarUserComponentProps {
  name?: string;
  email?: string;
  initials?: string;
  onClick?: (() => void) | null;
  className?: string;
}

// ---------------------------------------------------------------------------
// Sidebar Component
// Main navigation sidebar used across the dashboard
// ---------------------------------------------------------------------------

export const Sidebar: React.FC<SidebarProps> = ({
  showAdminSection = true,
  showOverviewSection = true,
  showManagementSection = true,
  showCampaignSwitcher = true,
  user = null,
  onLogout = null,
  className = "",
  // isMobileOpen and onMobileToggle are kept for backwards compat but ignored —
  // mobile open/close state is managed internally.
  isMobileOpen: _isMobileOpenProp,
  onMobileToggle: _onMobileToggleProp,
  collapsed = false,
  collapsible = false,
  onCollapsedChange,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signOut } = useClerk();
  const { user: clerkUser } = useUser();
  const { canAccessPage, allowedCampaigns, hasCampaigns, role, tenantSlug: userTenantSlug } = usePageAccess();
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const handleMobileToggle = (): void => setIsMobileOpen((v) => !v);
  const closeMobileSidebar = useCallback(() => setIsMobileOpen(false), []);

  // Try new URL params first, fall back to query params
  const params = useParams<{ tenantSlug?: string; campaignKey?: string }>();

  // Persist the last tenant+campaign seen in the URL so buildLink() keeps
  // generating valid /t/:slug/... paths when the user navigates to a global
  // admin route where tenantSlug/campaignKey are absent from params.
  const lastKnownTenantRef = useRef<{ tenantSlug: string; campaignKey: string } | null>(null);
  if (params.tenantSlug && params.campaignKey) {
    lastKnownTenantRef.current = { tenantSlug: params.tenantSlug, campaignKey: params.campaignKey };
  }

  const firstAllowedKey = allowedCampaigns?.length
    ? generateCampaignKey(allowedCampaigns[0])
    : null;

  const tenantSlug =
    params.tenantSlug ||
    lastKnownTenantRef.current?.tenantSlug ||
    userTenantSlug ||
    null;

  const campaignKey =
    params.campaignKey ||
    searchParams.get("campaign") ||
    lastKnownTenantRef.current?.campaignKey ||
    firstAllowedKey;

  // Build nav links — tenant-aware or legacy
  const buildLink = (segment: string): string => {
    if (segment === "media-vault" || segment === "documents" || segment === "settings" || segment === "support") {
      const campaignQuery = campaignKey ? `?campaign=${campaignKey}` : "";
      const pathMap: Record<string, string> = {
        "media-vault": "/media-vault",
        documents: tenantSlug ? `/t/${tenantSlug}/documents` : "/documents",
        settings: tenantSlug ? `/t/${tenantSlug}/settings/account` : "/settings",
        support: "/support",
      };
      return `${pathMap[segment]}${campaignQuery}`;
    }

    if (tenantSlug && campaignKey) {
      return `/t/${tenantSlug}/${segment}/${campaignKey}`;
    }
    // Legacy fallback
    const campaignQuery = campaignKey ? `?campaign=${campaignKey}` : "";
    const pathMap: Record<string, string> = {
      dashboard: "/dashboard",
      prospects: "/prospects",
      "construct-connect-crm": "/construct-connect-crm",
      conversations: "/conversations",
      content: "/generated-content",
      "social-listening": "/social-listening",
      "advertiser-verification": "/advertiser-verification",
      "media-vault": "/media-vault",
      documents: tenantSlug ? `/t/${tenantSlug}/documents` : "/documents",
      settings: tenantSlug ? `/t/${tenantSlug}/settings/account` : "/settings",
      support: "/support",
    };
    return `${pathMap[segment] || `/${segment}`}${campaignQuery}`;
  };

  const brandLink = tenantSlug
    ? `/t/${tenantSlug}`
    : `/dashboard${campaignKey ? `?campaign=${campaignKey}` : ""}`;

  const handleLogout = (): void => {
    if (onLogout) {
      onLogout();
    } else {
      signOut(() => navigate("/login"));
    }
  };

  const isActive = (path: string): boolean => location.pathname === path;
  const isActiveSegment = (segment: string): boolean => {
    if (tenantSlug) {
      return location.pathname.includes(`/${segment}/`);
    }
    const pathMap: Record<string, string> = {
      dashboard: "/dashboard",
      prospects: "/prospects",
      "construct-connect-crm": "/construct-connect-crm",
      conversations: "/conversations",
      content: "/generated-content",
      "social-listening": "/social-listening",
      "advertiser-verification": "/advertiser-verification",
    };
    return isActive(pathMap[segment] || `/${segment}`);
  };



  return (
    <>
      {/* Mobile hamburger button — hidden above 768px via CSS */}
      <button
        className="mobile-hamburger"
        onClick={handleMobileToggle}
        aria-label="Toggle navigation menu"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>
      {/* Mobile overlay — dims the page behind the open sidebar */}
      {isMobileOpen && (
        <div className="mobile-sidebar-overlay" onClick={closeMobileSidebar} />
      )}
    <div
      className={cn(
        "dashboard-sidebar flex h-screen flex-col bg-sidebar text-sidebar-foreground",
        isMobileOpen && "mobile-open",
        collapsed && "dashboard-sidebar--collapsed",
        className,
      )}
    >
      {/* Brand Section */}
      <div className="sidebar-brand">
        <Link to={brandLink} style={{ textDecoration: "none" }}>
          <span
            className="inline-block px-2.5 py-1 rounded-md border border-white/10 font-semibold tracking-tight select-none"
            style={{ background: "#000", color: "#fff", fontSize: "15px", letterSpacing: "-0.01em" }}
          >
            promota<span className="text-primary">.</span>
          </span>
        </Link>
        {collapsible && (
          <button
            type="button"
            className="sidebar-collapse-toggle"
            onClick={() => onCollapsedChange?.(!collapsed)}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? (
              <ChevronDoubleRightIcon className="h-4 w-4" />
            ) : (
              <ChevronDoubleLeftIcon className="h-4 w-4" />
            )}
          </button>
        )}
      </div>

      <div className="sidebar-scroll">
      {/* Admin Section */}
      {showAdminSection && role === "admin" && (
        <div className="sidebar-section">
          <div className="sidebar-section-title">ADMIN</div>
          {canAccessPage("active-campaigns") && (
            <SidebarNavItem
              to="/active-campaigns"
              icon={<BuildingOffice2Icon className="heroicon" />}
              label="Clients"
              isActive={isActive("/active-campaigns")}
              onClick={closeMobileSidebar}
            />
          )}
          {canAccessPage("users") && (
            <SidebarNavItem
              to="/users"
              icon={<UsersIcon className="heroicon" />}
              label="Users"
              isActive={isActive("/users")}
              onClick={closeMobileSidebar}
            />
          )}
          <SidebarNavItem
            to="/admin/feature-flags"
            icon={<FlagIcon className="heroicon" />}
            label="Feature Flags"
            isActive={isActive("/admin/feature-flags")}
            onClick={closeMobileSidebar}
          />
          {canAccessPage("event-monitor") && (
            <SidebarNavItem
              to="/event-monitor"
              icon={<SignalIcon className="heroicon" />}
              label="Event Monitor"
              isActive={location.pathname === "/event-monitor"}
              onClick={closeMobileSidebar}
            />
          )}
        </div>
      )}

      {/* Overview Section */}
      {showOverviewSection && (canAccessPage("dashboard") || canAccessPage("conversations") || canAccessPage("prospects") || canAccessPage("media-vault") || canAccessPage("documents") || canAccessPage("generated-content") || canAccessPage("ad-performance") || canAccessPage("social-listening") || canAccessPage("advertiser-verification")) && (
        <div className="sidebar-section">
          {tenantSlug && (
            <SidebarNavItem
              to={`/t/${tenantSlug}`}
              icon={<RocketLaunchIcon className="heroicon" />}
              label="Campaigns"
              isActive={location.pathname === `/t/${tenantSlug}`}
              onClick={closeMobileSidebar}
            />
          )}
          {canAccessPage("dashboard") && (
            <SidebarNavItem
              to={buildLink("dashboard")}
              icon={<ChartBarIcon className="heroicon" />}
              label="Overview"
              isActive={isActiveSegment("dashboard")}
              onClick={closeMobileSidebar}
            />
          )}
          {canAccessPage("conversations") && hasCampaigns && (
            <SidebarNavItem
              to={buildLink("conversations")}
              icon={<ChatBubbleLeftRightIcon className="heroicon" />}
              label="Conversations"
              isActive={isActiveSegment("conversations")}
              onClick={closeMobileSidebar}
            />
          )}
          {(canAccessPage("construct-connect-crm") || canAccessPage("prospects")) && hasCampaigns && (
            <SidebarNavItem
              to={buildLink("prospects")}
              icon={<UserGroupIcon className="heroicon" />}
              label="Prospects"
              isActive={isActiveSegment("prospects")}
              onClick={closeMobileSidebar}
            />
          )}
          {hasCampaigns && (
            <SidebarNavItem
              to={buildLink("construct-connect-crm")}
              icon={<ClipboardDocumentCheckIcon className="heroicon" />}
              label="Lead CRM"
              isActive={isActiveSegment("construct-connect-crm")}
              badge={<span className="rounded-full bg-blue-500/20 px-1.5 py-0.5 text-[10px] font-bold text-blue-200">Pilot</span>}
              onClick={closeMobileSidebar}
            />
          )}
          {canAccessPage("media-vault") && hasCampaigns && (
            <SidebarNavItem
              to={buildLink("media-vault")}
              icon={<FolderIcon className="heroicon" />}
              label="Media"
              isActive={isActive("/media-vault")}
              onClick={closeMobileSidebar}
            />
          )}
          {canAccessPage("documents") && tenantSlug && (
            <SidebarNavItem
              to={buildLink("documents")}
              icon={<DocumentTextIcon className="heroicon" />}
              label="Reports"
              isActive={location.pathname.includes("/documents")}
              onClick={closeMobileSidebar}
            />
          )}
          {canAccessPage("generated-content") && hasCampaigns && (
            <SidebarNavItem
              to={buildLink("content")}
              icon={<DocumentTextIcon className="heroicon" />}
              label="Content"
              isActive={isActiveSegment("content")}
              onClick={closeMobileSidebar}
            />
          )}
          {canAccessPage("ad-performance") && hasCampaigns && (
            <SidebarNavItem
              to={buildLink("ad-performance")}
              icon={<PresentationChartBarIcon className="heroicon" />}
              label="Ad Performance"
              isActive={isActiveSegment("ad-performance")}
              onClick={closeMobileSidebar}
            />
          )}
          {canAccessPage("social-listening") && hasCampaigns && (
            <SidebarNavItem
              to={buildLink("social-listening")}
              icon={<MagnifyingGlassCircleIcon className="heroicon" />}
              label="Social Interactions"
              isActive={isActiveSegment("social-listening")}
              onClick={closeMobileSidebar}
            />
          )}
        </div>
      )}
      </div>

      {/* Bottom group — Management (Settings/Support) clamped directly
          above the campaign switcher + user avatar. Single mt-auto on
          the wrapper so the entire group hugs the bottom even when
          Management is hidden by RBAC. */}
      <div className="sidebar-bottom mt-auto">
        {showManagementSection && (canAccessPage("settings") || canAccessPage("support")) && (
          <div className="sidebar-section">
            {canAccessPage("settings") && (
              <SidebarNavItem
                to={buildLink("settings")}
                icon={<Cog6ToothIcon className="heroicon" />}
                label="Settings"
                isActive={location.pathname.includes("/settings")}
                onMouseEnter={() => {
                  import("../../pages/AccountManagement");
                  import("../../pages/DisplaySettings");
                  import("../../pages/SettingsIntegrations");
                  import("../../pages/SettingsPlan");
                  import("../../pages/SettingsInvoices");
                }}
                onClick={closeMobileSidebar}
              />
            )}
            {canAccessPage("support") && (
              <SidebarNavItem
                to={buildLink("support")}
                icon={<ChatBubbleLeftRightIcon className="heroicon" />}
                label="Support"
                isActive={isActive("/support")}
                disabled
              />
            )}
          </div>
        )}

      <div>
        {/* Campaign Switcher */}
        {showCampaignSwitcher && allowedCampaigns && allowedCampaigns.length > 0 && (
          <div className="border-t border-white/10 px-3 py-3 text-left">
            <label className="mb-1.5 block text-left text-[10px] font-semibold uppercase tracking-wider text-sidebar-foreground/50">
              Campaign
            </label>
            <div className="relative">
              <select
                value={campaignKey || ""}
                onChange={(e) => {
                  const newCampaign = e.target.value;
                  if (tenantSlug) {
                    const segments = location.pathname.split("/");
                    if (segments.length >= 5) {
                      segments[4] = newCampaign;
                    } else if (segments.length === 4) {
                      segments.push(newCampaign);
                    }
                    navigate(segments.join("/"));
                  } else {
                    navigate(`${location.pathname}?campaign=${newCampaign}`, { replace: true });
                  }
                }}
                className="w-full cursor-pointer appearance-none rounded-md border border-white/10 bg-white/5 py-1.5 pl-2.5 pr-8 text-sm text-sidebar-foreground outline-none transition-colors hover:bg-white/10 focus:border-white/20"
              >
                <option value="__all__">All Campaigns</option>
                {allowedCampaigns.map((rawKey) => {
                  const formattedKey = generateCampaignKey(rawKey);
                  const label = friendlyCampaignName(rawKey);
                  return (
                    <option key={rawKey} value={formattedKey}>{label}</option>
                  );
                })}
              </select>
              <ChevronUpDownIcon className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-sidebar-foreground/50" />
            </div>
          </div>
        )}

        {/* User Section — Clerk user */}
        <div
          className="flex cursor-pointer items-center gap-3 border-t border-white/10 px-4 py-3 text-left transition-colors hover:bg-sidebar-accent"
          onClick={() => setShowProfileDialog(true)}
          title="Edit profile"
        >
        {clerkUser?.imageUrl ? (
          <img
            src={clerkUser.imageUrl}
            alt=""
            className="h-8 w-8 flex-shrink-0 rounded-full object-cover"
          />
        ) : (
          <div className="user-avatar">
            {clerkUser?.firstName?.[0] || user?.initials || "U"}
          </div>
        )}
        <div className="user-info min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-sidebar-foreground">{clerkUser?.fullName || user?.name || "User"}</div>
          <div className="truncate text-xs text-sidebar-foreground/50">{clerkUser?.primaryEmailAddress?.emailAddress || user?.email || ""}</div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); handleLogout(); }}
          className="flex-shrink-0 rounded-md p-1.5 text-sidebar-foreground/40 transition-colors hover:bg-red-500/10 hover:text-red-400"
          title="Sign out"
        >
          <ArrowRightStartOnRectangleIcon className="h-4 w-4" />
        </button>
        </div>
      </div>
      </div>

      {/* Profile Edit Dialog */}
      <ProfileDialog
        isOpen={showProfileDialog}
        onClose={() => setShowProfileDialog(false)}
      />
    </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// ProfileDialog Component
// Simple dialog for editing Clerk profile
// ---------------------------------------------------------------------------

const ProfileDialog: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const { user: clerkUser } = useUser();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync form with Clerk user when dialog opens
  React.useEffect(() => {
    if (isOpen && clerkUser) {
      setFirstName(clerkUser.firstName || "");
      setLastName(clerkUser.lastName || "");
      setError(null);
    }
  }, [isOpen, clerkUser]);

  const handleSave = async () => {
    if (!clerkUser) return;
    setSaving(true);
    setError(null);
    try {
      await clerkUser.update({ firstName, lastName });
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!clerkUser || !e.target.files?.[0]) return;
    setSaving(true);
    setError(null);
    try {
      await clerkUser.setProfileImage({ file: e.target.files[0] });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to upload image");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={onClose}
      style={{
        background: "white",
        borderRadius: "12px",
        padding: "24px",
        width: "100%",
        maxWidth: "400px",
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
      }}
    >
      <h2 style={{ margin: "0 0 20px", fontSize: "18px", fontWeight: 600 }}>Edit Profile</h2>

      {/* Avatar */}
      <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "20px" }}>
        <div style={{ position: "relative" }}>
          {clerkUser?.imageUrl ? (
            <img
              src={clerkUser.imageUrl}
              alt=""
              style={{ width: "56px", height: "56px", borderRadius: "50%", objectFit: "cover" }}
            />
          ) : (
            <div style={{
              width: "56px", height: "56px", borderRadius: "50%",
              background: "#5485b1", color: "white",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "20px", fontWeight: 600,
            }}>
              {clerkUser?.firstName?.[0] || "U"}
            </div>
          )}
        </div>
        <label style={{
          cursor: "pointer", fontSize: "13px", color: "#5485b1",
          fontWeight: 500,
        }}>
          Change photo
          <input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            style={{ display: "none" }}
          />
        </label>
      </div>

      {/* Fields */}
      <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "20px" }}>
        <div>
          <label style={{ display: "block", fontSize: "13px", fontWeight: 500, marginBottom: "4px", color: "#374151" }}>
            First name
          </label>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            style={{
              width: "100%", padding: "8px 12px", borderRadius: "6px",
              border: "1px solid #d1d5db", fontSize: "14px", outline: "none",
              boxSizing: "border-box",
            }}
          />
        </div>
        <div>
          <label style={{ display: "block", fontSize: "13px", fontWeight: 500, marginBottom: "4px", color: "#374151" }}>
            Last name
          </label>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            style={{
              width: "100%", padding: "8px 12px", borderRadius: "6px",
              border: "1px solid #d1d5db", fontSize: "14px", outline: "none",
              boxSizing: "border-box",
            }}
          />
        </div>
        <div>
          <label style={{ display: "block", fontSize: "13px", fontWeight: 500, marginBottom: "4px", color: "#374151" }}>
            Email
          </label>
          <input
            type="text"
            value={clerkUser?.primaryEmailAddress?.emailAddress || ""}
            disabled
            style={{
              width: "100%", padding: "8px 12px", borderRadius: "6px",
              border: "1px solid #e5e7eb", fontSize: "14px", color: "#9ca3af",
              background: "#f9fafb", boxSizing: "border-box",
            }}
          />
        </div>
      </div>

      {error && (
        <p style={{ color: "#dc2626", fontSize: "13px", margin: "0 0 12px" }}>{error}</p>
      )}

      {/* Actions */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
        <button
          onClick={onClose}
          disabled={saving}
          style={{
            padding: "8px 16px", borderRadius: "6px", border: "1px solid #d1d5db",
            background: "white", fontSize: "14px", cursor: "pointer",
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: "8px 16px", borderRadius: "6px", border: "none",
            background: "#5485b1", color: "white", fontSize: "14px",
            cursor: saving ? "not-allowed" : "pointer",
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </AnimatedModal>
  );
};

// ---------------------------------------------------------------------------
// SidebarNavItem Component
// Individual navigation item in the sidebar
// ---------------------------------------------------------------------------

export const SidebarNavItem: React.FC<SidebarNavItemProps> = ({
  to,
  icon,
  label,
  isActive = false,
  onClick = null,
  onMouseEnter,
  external = false,
  badge = null,
  disabled = false,
}) => {
  const className = cn(
    "sidebar-nav-item relative flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
    disabled
      ? "opacity-40 pointer-events-none"
      : isActive
        ? "text-foreground"
        : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
  );

  const soonBadge = disabled ? (
    <span
      className="relative ml-auto rounded bg-gray-500/20 px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none text-gray-400"
      style={{ zIndex: 1 }}
    >
      Soon
    </span>
  ) : null;

  const activeIndicator = isActive && !disabled ? (
    <motion.div
      layoutId="sidebar-active"
      className="absolute inset-0 rounded-md bg-primary/10"
      style={{ zIndex: 0 }}
      transition={{ type: "spring", stiffness: 350, damping: 30 }}
    />
  ) : null;

  const content = (
    <>
      {activeIndicator}
      <span className="nav-icon relative" style={{ zIndex: 1 }}>{icon}</span>
      <span className="nav-label relative" style={{ zIndex: 1 }}>{label}</span>
      {badge && <span className="nav-badge relative" style={{ zIndex: 1 }}>{badge}</span>}
      {soonBadge}
    </>
  );

  if (disabled) {
    return (
      <div className={className} title={label}>
        {content}
      </div>
    );
  }

  if (external) {
    return (
      <a
        href={to}
        className={className}
        title={label}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick ?? undefined}
      >
        {content}
      </a>
    );
  }

  if (onClick && !to) {
    return (
      <div
        className={className}
        title={label}
        onClick={onClick}
        style={{ cursor: "pointer" }}
      >
        {content}
      </div>
    );
  }

  return (
    <Link to={to || ""} className={className} title={label} onClick={onClick ?? undefined} onMouseEnter={onMouseEnter}>
      {content}
    </Link>
  );
};

// ---------------------------------------------------------------------------
// SidebarSection Component
// A section within the sidebar with title and children
// ---------------------------------------------------------------------------

export const SidebarSection: React.FC<SidebarSectionProps> = ({ title, children, className = "" }) => {
  return (
    <div className={`sidebar-section ${className}`}>
      {title && <div className="sidebar-section-title">{title}</div>}
      {children}
    </div>
  );
};

// ---------------------------------------------------------------------------
// SidebarBrand Component
// Brand/logo section of the sidebar
// ---------------------------------------------------------------------------

export const SidebarBrand: React.FC<SidebarBrandProps> = ({
  logo = `${process.env.PUBLIC_URL || ""}/promota-logo.png`,
  alt = "Logo",
  linkTo = "/dashboard",
  className = "",
}) => {
  return (
    <div className={`sidebar-brand ${className}`}>
      <Link
        to={linkTo}
        style={{
          textDecoration: "none",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div className="brand-icon">
          <img src={logo} alt={alt} />
        </div>
      </Link>
    </div>
  );
};

// ---------------------------------------------------------------------------
// SidebarUser Component
// User profile section of the sidebar
// ---------------------------------------------------------------------------

export const SidebarUser: React.FC<SidebarUserComponentProps> = ({
  name = "Dashboard",
  email = "",
  initials = "D",
  onClick = null,
  className = "",
}) => {
  return (
    <div
      className={`sidebar-user ${className}`}
      onClick={onClick ?? undefined}
      style={{ cursor: onClick ? "pointer" : "default" }}
    >
      <div className="user-avatar">{initials}</div>
      <div className="user-info">
        <div className="user-name">{name}</div>
        <div className="user-email">{email}</div>
      </div>
      {onClick && (
        <div className="user-arrow">
          <ChevronRightIcon className="heroicon" />
        </div>
      )}
    </div>
  );
};

export default Sidebar;
